import { SpawnType } from "@1games-ru/region-shared/dist/enums/selectSpawn/type";
import { isNumber } from "lodash";
import { useCallback, useEffect } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector.ts";
import { useKeyPress } from "@/core/keyPressManager.tsx";
import { setActiveKey } from "@/domains/activeKey/slice";
import { hoverSpawnTypeIndexSelector, spawnTypesSelector } from "@/domains/spawn/slice";
import useHandlers from "@/domains/spawn/hooks/useHandlers";
import KeyCode from "@/assets/constants/key-code";

export default function useKeyboardEvent() {
  const dispatch = useAppDispatch();
  const hoverSpawnTypeIndex = useAppSelector(hoverSpawnTypeIndexSelector);
  const spawnTypes = useAppSelector(spawnTypesSelector);
  const {
    confirmSpawnHandler,
    setActiveSpawnHandler,
    setHoverSpawnHandler,
    nextHoverSpawnHandler,
    prevHoverSpawnHandler,
  } = useHandlers();
  const { subscribeKeyDown, subscribeKeyUp, unsubscribeKeyDown, unsubscribeKeyUp } = useKeyPress();

  const handleKeyDown = useCallback(
    async (keyCode: KeyCode) => {
      if (
        [
          KeyCode.LEFT_ARROW,
          KeyCode.RIGHT_ARROW,
          KeyCode.KEY_1,
          KeyCode.KEY_2,
          KeyCode.KEY_3,
          KeyCode.KEY_4,
          KeyCode.KEY_5,
          KeyCode.KEY_6,
          KeyCode.ENTER,
          KeyCode.ESCAPE,
        ].includes(keyCode)
      ) {
        console.log(`KEYCODE ${keyCode}`);
        dispatch(setActiveKey(keyCode));

        switch (keyCode) {
          case KeyCode.LEFT_ARROW: {
            await prevHoverSpawnHandler();
            break;
          }
          case KeyCode.RIGHT_ARROW: {
            await nextHoverSpawnHandler();
            break;
          }
          case KeyCode.KEY_1:
          case KeyCode.KEY_2:
          case KeyCode.KEY_3:
          case KeyCode.KEY_4:
          case KeyCode.KEY_5:
          case KeyCode.KEY_6: {
            const indexMap = {
              [KeyCode.KEY_1]: 0,
              [KeyCode.KEY_2]: 1,
              [KeyCode.KEY_3]: 3,
              [KeyCode.KEY_4]: 4,
              [KeyCode.KEY_5]: 5,
              [KeyCode.KEY_6]: 6,
            }[keyCode];

            if (isNumber(spawnTypes[indexMap])) {
              await setActiveSpawnHandler(spawnTypes[indexMap]);
            }
            break;
          }

          case KeyCode.ENTER: {
            await confirmSpawnHandler();
            break;
          }
          case KeyCode.ESCAPE: {
            await setActiveSpawnHandler(SpawnType.Undefined);
            break;
          }
          default:
            break;
        }
      }
    },
    [
      confirmSpawnHandler,
      dispatch,
      setActiveSpawnHandler,
      spawnTypes,
      setHoverSpawnHandler,
      hoverSpawnTypeIndex,
    ],
  );

  const handleKeyUp = useCallback(() => {
    dispatch(setActiveKey());
  }, [dispatch]);

  useEffect(() => {
    if (spawnTypes.length === 0) return () => {};

    subscribeKeyUp("spawn", handleKeyUp);
    subscribeKeyDown("spawn", handleKeyDown);

    return () => {
      unsubscribeKeyUp("spawn");
      unsubscribeKeyDown("spawn");
    };
  }, [
    subscribeKeyDown,
    subscribeKeyUp,
    handleKeyDown,
    handleKeyUp,
    unsubscribeKeyUp,
    unsubscribeKeyDown,
    spawnTypes.length,
  ]);
}
